// OrderTracking.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Avatar
} from '@mui/material';
import {
  CheckCircle,
  LocalShipping,
  LocationOn,
  Phone,
  Email,
  Schedule
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { orderAssignmentAPI } from './deliveryAgentAPI';

const OrderTracking = () => {
  const { orderId, orderType } = useParams();
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    fetchOrderDetails();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchOrderDetails, 30000);
    return () => clearInterval(interval);
  }, [orderId, orderType]);

  const fetchOrderDetails = async () => {
    try {
      // Fetch order details from API
      // This would need to be implemented in your backend
      setLoading(false);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setLoading(false);
    }
  };

  const getSteps = () => {
    if (orderType === 'medicine') {
      return [
        {
          label: 'Order Confirmed',
          description: 'Your order has been confirmed and is being prepared',
          status: 'completed'
        },
        {
          label: 'Agent Assigned',
          description: 'A delivery agent has been assigned to your order',
          status: 'completed'
        },
        {
          label: 'Out for Delivery',
          description: 'Your order is on the way',
          status: 'active'
        },
        {
          label: 'Delivered',
          description: 'Order delivered successfully',
          status: 'pending'
        }
      ];
    } else {
      return [
        {
          label: 'Booking Confirmed',
          description: 'Your booking has been confirmed',
          status: 'completed'
        },
        {
          label: 'Agent Assigned',
          description: 'A sample collection agent has been assigned',
          status: 'completed'
        },
        {
          label: 'Agent En Route',
          description: 'Agent is on the way to collect your sample',
          status: 'active'
        },
        {
          label: 'Sample Collected',
          description: 'Sample collected successfully',
          status: 'pending'
        }
      ];
    }
  };

  const steps = getSteps();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#006D77' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #006D77 0%, #005A63 100%)' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <LocalShipping sx={{ fontSize: 48, color: '#fff' }} />
          <Box>
            <Typography variant="h4" color="#fff" fontWeight={600}>
              Track Your Order
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.8)">
              Order ID: {orderId}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '2fr 1fr' }} gap={3}>
        {/* Tracking Timeline */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={3}>
            Delivery Status
          </Typography>
          
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label} completed={step.status === 'completed'}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      color: step.status === 'completed' ? '#28a745' : 
                             step.status === 'active' ? '#006D77' : '#ccc',
                      '&.Mui-active': { color: '#006D77' },
                      '&.Mui-completed': { color: '#28a745' }
                    }
                  }}
                >
                  <Typography variant="body1" fontWeight={600}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                  {step.status === 'active' && (
                    <Chip
                      label="In Progress"
                      size="small"
                      color="primary"
                      sx={{ mt: 1 }}
                    />
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Agent Details & Actions */}
        <Stack spacing={3}>
          {/* Agent Info */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Your Delivery Agent
            </Typography>
            
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: '#006D77' }}>
                {orderData?.deliveryAgent?.name?.charAt(0) || 'A'}
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {orderData?.deliveryAgent?.name || 'Agent Name'}
                </Typography>
                <Chip
                  label={orderType === 'medicine' ? 'Delivery' : 'Sample Collection'}
                  size="small"
                  color="primary"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Stack>

            <Stack spacing={1.5} mt={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Phone sx={{ fontSize: 20, color: '#666' }} />
                <Typography variant="body2">
                  {orderData?.deliveryAgent?.phone || '+91 XXXXXXXXXX'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <LocalShipping sx={{ fontSize: 20, color: '#666' }} />
                <Typography variant="body2">
                  {orderData?.deliveryAgent?.vehicleType?.toUpperCase() || 'BIKE'}
                </Typography>
              </Stack>
            </Stack>

            <Button
              fullWidth
              variant="contained"
              startIcon={<Phone />}
              href={`tel:${orderData?.deliveryAgent?.phone || ''}`}
              sx={{
                mt: 2,
                bgcolor: '#006D77',
                '&:hover': { bgcolor: '#005A63' }
              }}
            >
              Call Agent
            </Button>
          </Paper>

          {/* Order Details */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Order Details
            </Typography>
            
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Service
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {orderData?.serviceName || 'Service Name'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Amount
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  ₹{orderData?.totalAmount?.toFixed(2) || '0.00'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Delivery Address
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {orderData?.customerAddress?.street || 'Address Line 1'}
                  <br />
                  {orderData?.customerAddress?.city || 'City'}, {orderData?.customerAddress?.state || 'State'}
                  <br />
                  {orderData?.customerAddress?.pincode || 'Pincode'}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Expected Time */}
          <Paper sx={{ p: 3, bgcolor: '#e8f5e9', border: '1px solid #28a745' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Schedule sx={{ color: '#28a745', fontSize: 32 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Expected {orderType === 'medicine' ? 'Delivery' : 'Collection'}
                </Typography>
                <Typography variant="h6" fontWeight={600} color="#28a745">
                  30-45 minutes
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
};

export default OrderTracking;